import { DocumentCitationStyleLabels, type DocumentConfig,DocumentLanguageLabels } from '@alpod-ai-writer/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Label, Modal, type ModalProps,Select,Textarea } from 'flowbite-react';
import { AlertTriangleIcon, CheckIcon, LifeBuoyIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import { sortedDocumentTypeKeys } from '../../../lib/template.utils';

interface FormValues {
    templateId: string;
    documentType: string;
    message: string;
}

export interface RequestTemplateChangeModalProps extends ModalProps {
    documentId: string;
}

export const RequestTemplateChangeModal = ({
    documentId,
    show,
    onClose
}: RequestTemplateChangeModalProps) => {
    const api = useApi();

    const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>({
        defaultValues: {
            message: ''
        }
    });

    const templateId = watch('templateId');

    const templateList = useQuery({
        queryKey: [ 'documentTemplateList' ],
        queryFn: () => api.v1.templates.getAll()
    });

    const templateDetail = useQuery({
        queryKey: [ 'documentTemplate', templateId ],
        enabled: !!templateId,
        queryFn: () => api.v1.templates.getById(templateId)
    });

    const submitFeedback = useMutation({
        mutationFn: (data: FormValues) => api.v1.productSupport.submitFeedback({
            category: 'Změna šablony',
            documentId,
            documentSection: null,
            message: data.message,
            fields: {
                'Šablona': templateDetail.data?.label ?? data.templateId,
                'Typ dokumentu': templateDetail.data?.config?.types[data.documentType]?.label ?? data.documentType
            },
            locationInApp: 'Nastavení dokumentu'
        })
    });

    const onSubmit = async (data: FormValues) => {
        submitFeedback.mutate(data);
    };

    const documentType = watch('documentType');
    const templateConfig = templateDetail.data?.config as DocumentConfig|undefined;
    const templateTypeConfig = templateConfig?.types[documentType];

    return (
        <Modal show={show} size="3xl" onClose={onClose} popup>
            <Modal.Header>
                <span className="flex flex-row items-start md:items-center leading-5 justify-center gap-2 p-3 text-base md:text-lg font-semibold text-gray-800 dark:text-gray-400">
                    <LifeBuoyIcon size={20} />
                    <span>Změna šablony</span>
                </span>
            </Modal.Header>
            <Modal.Body>
                {!submitFeedback.isSuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
                        <div>
                            <Label htmlFor="templateId" className="block mb-1">Vyberte šablonu podle Vaší školy a fakulty</Label>
                            <Select
                                id="templateId"
                                {...register('templateId', { required: 'Šablona je povinná.' })}
                                color={errors.templateId ? 'failure' : 'default'}
                                helperText={(
                                    <>
                                        {errors.templateId && (
                                            <span className='block'>
                                                {errors.templateId.message}
                                            </span>
                                        )}
                                    </>
                                )}
                            >
                                <option value='' disabled selected>Vyberte šablonu</option>
                                {templateList.data?.items.map((template) => (
                                    <option key={template.templateId} value={template.templateId}>{template.label}</option>    
                                ))}
                            </Select>

                            {templateDetail.data && !templateDetail.data.docxPath && (
                                <p className='flex flex-row item-center gap-2 text-red-600 font-semibold'>
                                    <AlertTriangleIcon size={20} />
                                    <span>Pozor: Tato šablona nemá funkční konfiguraci.</span>
                                </p>
                            )}
                        </div>

                        <div>
                            <Label htmlFor="documentType" className="block mb-1">Typ dokumentu</Label>
                            <Select
                                id="documentType"
                                {...register('documentType', { required: 'Typ dokumentu je povinný.' })}
                                color={errors.documentType ? 'failure' : 'default'}
                                disabled={!templateConfig}
                                helperText={(
                                    <>
                                        {errors.documentType && (
                                            <span className='block'>
                                                {errors.documentType.message}
                                            </span>
                                        )}
                                    </>
                                )}
                            >
                                <option value='' disabled selected>Vyberte typ dokumentu</option>
                                {templateConfig?.types && sortedDocumentTypeKeys(templateConfig.types).map((type) => (
                                    <option key={type} value={type}>{templateConfig.types[type].label}</option>
                                ))}
                            </Select>
                        </div>

                        {templateConfig && templateTypeConfig && (
                            <div className='flex flex-col md:flex-row md:items-center justify-between gap-2 gap-y-4 text-sm p-4 border border-gray-300 rounded-md'>
                                <div className='flex flex-col gap-1'>
                                    <span className='font-semibold'>Jazyk dokumentu</span>
                                    <span className='font-medium'>{DocumentLanguageLabels[templateConfig.language] ?? templateConfig.language}</span>
                                </div>
                                <div className='flex flex-col gap-1'>
                                    <span className='font-semibold'>Rozsah</span>
                                    <span className='font-medium'>
                                        {templateTypeConfig.minPages}
                                        {templateTypeConfig.maxPages ? ` - ${templateTypeConfig.maxPages}` : ''} normostran
                                    </span>
                                </div>
                                <div className='flex flex-col gap-1'>
                                    <span className='font-semibold'>Citační norma</span>
                                    <span className='font-medium'>{DocumentCitationStyleLabels[templateConfig.citationStyle] ?? templateConfig.citationStyle}</span>
                                </div>
                            </div>
                        )}

                        <div>
                            <Label htmlFor="message" className="block mb-1">Doplňující informace</Label>
                            <Textarea
                                id="message"
                                placeholder='Napište nám prosím důvod změny.'
                                {...register('message', {
                                    maxLength: { value: 30000, message: 'Maximální délka je 30 000 znaků' }
                                })}
                                rows={4}
                                color={errors.message ? 'failure' : 'default'}
                                helperText={(
                                    <>
                                        {errors.message && (
                                            <span className='block'>
                                                {errors.message.message}
                                            </span>
                                        )}
                                    </>
                                )}
                                disabled={submitFeedback.isPending || submitFeedback.isSuccess}
                            />
                        </div>
                    
                        <div className='flex flex-row items-center justify-end gap-2'>
                            <ErrorSwitch
                                error={submitFeedback.error}
                                default={{ message: 'Při odesílání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                                options={[]}
                                className='grow'
                            />

                            <Button
                                type='submit'
                                color='primary'
                                disabled={submitFeedback.isPending}
                            >Odeslat zprávu</Button>

                            <Button
                                color='gray'
                                onClick={onClose}
                            >Zavřít</Button>
                        </div>
                    </form>
                )}

                {submitFeedback.isSuccess && (
                    <div className='flex flex-col gap-4'>
                        <div className='flex flex-row items-center justify-center gap-2 p-4 border border-gray-300 rounded-lg'>
                            <CheckIcon size={32} className='text-lime-600' />
                            <p className='font-medium text-lime-700'>Vaše zpráva byla odeslána. Budeme Vás co nejdříve kontaktovat zpět.</p>
                        </div>

                        <div className='flex flex-row items-center justify-center gap-2 flex-wrap'>
                            <Button
                                color='primary'
                                onClick={onClose}
                            >Zavřít</Button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};
